/**
 * Public OIDC configuration for the Dashboard's #3882 SSO client (T012, Part A).
 *
 * Per `contracts/release-packet.md` "Configuration Contract" and `contracts/oidc-provider.md`
 * "Dashboard Configuration Safety": the Dashboard registers capability-named public values
 * (authority/auth URL, public client ID, exact callback URI, optional silent callback URI, and
 * post-logout URI). These values are public, not secrets or flags, and normal `oidc-spa`
 * discovery supplies the protocol endpoints/issuer/JWKS metadata from the authority.
 *
 * Design note (configuration source): the values are read through the `ENVIRONMENT` accessor
 * (`src/constants/env-constants.ts`), which exposes them from the environment module as build-time
 * `SsoEnvironment` getters. They are not `EnvironmentObject` fields: that type is owned by
 * `@rapid-recovery-agency-inc/insightt-shared` and can be swapped at runtime by the environment
 * switcher, while OIDC settings must always come from the running build (Constitution VII).
 *
 * Design note (validation API shape): this module exports both `validateOidcConfig()` (a
 * non-throwing result, `{ valid, config } | { valid: false, errors }`) and `getOidcConfig()` (a
 * throwing accessor built on top of it). `e2e/config/env-vars.ts`'s `validateEnvVars` throws
 * directly; that style suits a script that should crash fast in a Node/e2e process. Here a
 * non-throwing result is exposed too because FR-040/FR-041 fail-closed behavior must be provable
 * in unit tests without relying on `toThrow` message-matching, and because an eventual
 * `oidc-spa` integration (T022) needs a Boolean/enumerated signal it can render a recovery state
 * from, not just an exception. `getOidcConfig()` is kept for call sites (or scripts) that prefer
 * to fail fast with a clear thrown message, mirroring the `validateEnvVars` convention.
 */

import { ENVIRONMENT } from '../../constants/env-constants';
import { SSO_ENV_VAR_NAMES } from '../environment/environment-utils';

export interface OidcConfig {
  authority: string;
  clientId: string;
  callbackUri: string;
  /** Only required if the installed `oidc-spa` release's silent renewal flow needs it. */
  silentCallbackUri?: string;
  postLogoutUri: string;
}

export interface OidcConfigValid {
  valid: true;
  config: OidcConfig;
  errors: [];
}

export interface OidcConfigInvalid {
  valid: false;
  config: null;
  errors: string[];
}

export type OidcConfigValidationResult = OidcConfigValid | OidcConfigInvalid;

/** Exported read-only so `sso-env-contract.test.ts` can check code, types and docs agree. */
export const REQUIRED_FIELDS = [
  ['SSO_BASE_URL', 'authority'],
  ['SSO_CLIENT_ID', 'clientId'],
  ['SSO_CALLBACK_URI', 'callbackUri'],
  ['SSO_POST_LOGOUT_URI', 'postLogoutUri'],
] as const;

export const OPTIONAL_URI_FIELDS = [['SSO_SILENT_CALLBACK_URI', 'silentCallbackUri']] as const;

const URI_FIELD_KEYS = new Set(['authority', 'callbackUri', 'silentCallbackUri', 'postLogoutUri']);

/** Values that clearly signal a placeholder/wildcard rather than a real registered value. */
function containsWildcard(value: string): boolean {
  return value.includes('*');
}

/** Coarse heuristic: public config must never carry secret-shaped values (FR-041). */
function looksSecretLike(value: string): boolean {
  return /secret|api[-_]?key|password|token=/i.test(value);
}

function parseUrl(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

/**
 * Validates the raw `ENVIRONMENT.SSO_*` values and returns a discriminated
 * result instead of throwing, so callers (including tests) can assert fail-closed behavior
 * directly. Never returns a partially-valid config: any failure yields `valid: false`.
 */
/** Options for {@link validateOidcConfig}; injectable for tests. */
export interface OidcConfigValidationOptions {
  /**
   * The app root this build runs at (origin + `BASE_URL`). `oidc-spa` sends it as both the
   * `redirect_uri` and the `post_logout_redirect_uri`, so the configured redirect URIs must equal it.
   */
  readonly appRootUrl?: string;
}

/** The app root this page runs at (origin + `BASE_URL`), which `oidc-spa` sends as its redirect URIs. */
export const getAppRootUrl = (): string => new URL(ENVIRONMENT.APP_BASE_URL || '/', window.location.origin).href;

/** Compares URLs by origin and path, ignoring a trailing slash. */
const isSameLocation = (value: string, expected: string): boolean => {
  const actual = parseUrl(value);
  const target = parseUrl(expected);
  const path = (url: URL): string => url.pathname.replace(/\/+$/, '');
  return actual !== null && target !== null && actual.origin === target.origin && path(actual) === path(target);
};

/** The Dashboard-owned redirect URIs; each may be an explicit comma-separated allowlist (FR-040). */
const REDIRECT_FIELDS = ['callbackUri', 'silentCallbackUri', 'postLogoutUri'] as const;
type RedirectField = (typeof REDIRECT_FIELDS)[number];

/**
 * Splits a redirect URI value into its allowlisted entries. One build is both deployed and tested at
 * another origin (the Amplify e2e preview at `http://localhost:3000/`), so each redirect URI may list
 * every app root registered for it, separated by commas.
 */
const splitAllowlist = (value: string): string[] =>
  value
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);

/**
 * Environment binding (FR-004, FR-040, T068): one allowlisted entry of each redirect URI must be the
 * app root this build is actually running at, because that is what `oidc-spa` sends. That entry
 * becomes the configured value. A build running anywhere else fails closed instead of redirecting
 * users somewhere unintended.
 */
function bindRedirectUris(
  raw: Record<string, string | undefined>,
  appRootUrl: string,
): { errors: string[]; bound: Partial<Record<RedirectField, string>> } {
  const errors: string[] = [];
  const bound: Partial<Record<RedirectField, string>> = {};
  for (const fieldName of REDIRECT_FIELDS) {
    const value = raw[fieldName];
    if (value === undefined) continue;
    const entries = splitAllowlist(value).filter((entry) => parseUrl(entry) !== null);
    if (entries.length === 0) continue; // already reported as malformed
    const match = entries.find((entry) => isSameLocation(entry, appRootUrl));
    if (match) {
      bound[fieldName] = match;
    } else {
      errors.push(`OIDC ${fieldName} must be this app's root (${appRootUrl}), which oidc-spa sends: ${value}`);
    }
  }
  return { errors, bound };
}

/**
 * Checks each entry of one URI value: no wildcard, nothing secret-shaped, an absolute URL. Returns
 * the errors and the origins of the entries that passed.
 */
function checkUriEntries(
  fieldName: string,
  entries: string[],
  value: string,
): { errors: string[]; origins: Set<string> } {
  const errors: string[] = [];
  const origins = new Set<string>();
  if (entries.length === 0) {
    errors.push(`OIDC configuration value for ${fieldName} is not a valid absolute URL: ${value}`);
  }
  for (const entry of entries) {
    const parsed = parseUrl(entry);
    if (containsWildcard(entry)) {
      errors.push(`OIDC configuration value for ${fieldName} must not contain a wildcard: ${entry}`);
    } else if (looksSecretLike(entry)) {
      errors.push(`OIDC configuration value for ${fieldName} looks secret-like and is rejected: ${fieldName}`);
    } else if (!parsed) {
      errors.push(`OIDC configuration value for ${fieldName} is not a valid absolute URL: ${entry}`);
    } else {
      origins.add(parsed.origin);
    }
  }
  return { errors, origins };
}

export function validateOidcConfig(options: OidcConfigValidationOptions = {}): OidcConfigValidationResult {
  const errors: string[] = [];
  const raw: Record<string, string | undefined> = {};

  for (const [envKey, fieldName] of REQUIRED_FIELDS) {
    const value = ENVIRONMENT[envKey];
    raw[fieldName] = value;
    if (!value || !value.trim()) {
      errors.push(`Missing required OIDC configuration value: ${SSO_ENV_VAR_NAMES[envKey]}`);
    }
  }

  for (const [envKey, fieldName] of OPTIONAL_URI_FIELDS) {
    const value = ENVIRONMENT[envKey];
    if (value !== undefined) {
      raw[fieldName] = value;
    }
  }

  // Stop early on missing values: URL/consistency checks below assume presence.
  if (errors.length > 0) {
    return { valid: false, config: null, errors };
  }

  // The authority may legitimately live on a different (provider) origin. The Dashboard-owned
  // redirect URIs must allowlist the same environments as each other, so a mismatched deployment
  // target fails closed.
  const redirectOrigins: string[] = [];

  for (const fieldName of Array.from(URI_FIELD_KEYS)) {
    const value = raw[fieldName];
    if (value === undefined) {
      continue; // optional field not configured
    }

    const isRedirect = fieldName !== 'authority';
    const checked = checkUriEntries(fieldName, isRedirect ? splitAllowlist(value) : [value], value);
    errors.push(...checked.errors);
    if (isRedirect) {
      redirectOrigins.push(Array.from(checked.origins).sort().join(','));
    }
  }

  // clientId is not a URL; only check wildcard/secret-like shape for it.
  if (containsWildcard(raw.clientId as string)) {
    errors.push('OIDC configuration value for clientId must not contain a wildcard');
  }
  if (looksSecretLike(raw.clientId as string)) {
    errors.push('OIDC configuration value for clientId looks secret-like and is rejected');
  }

  const binding = bindRedirectUris(raw, options.appRootUrl ?? getAppRootUrl());
  errors.push(...binding.errors);

  if (new Set(redirectOrigins).size > 1) {
    errors.push(
      'OIDC redirect configuration is cross-environment/inconsistent: callback, silent callback, and ' +
        'post-logout URIs must allowlist the same origins',
    );
  }

  if (errors.length > 0) {
    return { valid: false, config: null, errors };
  }

  return {
    valid: true,
    errors: [],
    config: {
      authority: raw.authority as string,
      clientId: raw.clientId as string,
      callbackUri: binding.bound.callbackUri as string,
      silentCallbackUri: binding.bound.silentCallbackUri,
      postLogoutUri: binding.bound.postLogoutUri as string,
    },
  };
}

/**
 * Throwing accessor built on `validateOidcConfig()`. Prefer this at a call site that should fail
 * fast (mirroring `e2e/config/env-vars.ts`'s `validateEnvVars` convention) rather than branch on
 * a result object.
 */
export function getOidcConfig(options: OidcConfigValidationOptions = {}): OidcConfig {
  const result = validateOidcConfig(options);
  if (!result.valid) {
    throw new Error(`Invalid OIDC configuration: ${result.errors.join('; ')}`);
  }
  return result.config;
}
